import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import ClassifiedCard from '../components/ClassifiedCard';
import { CLASSIFIED_CATEGORIES, CLASSIFIED_TAXONOMY, categoryChildren, categoryPath, mergeCustomCategories, resolveCat } from '../data/classifiedsCategories';
import { regionLabel } from '../lib/regionNames';
import { Search, Plus, X, Tag, Loader2, ShoppingCart, ShieldCheck, Bookmark, BookmarkPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { clsCartCount, CLS_CART_EVENT } from '../lib/classifiedsCart';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LV_REGIONS = ['riga', 'jurmala', 'liepaja', 'ventspils', 'daugavpils', 'rezekne', 'jelgava', 'jekabpils', 'valmiera', 'ogre', 'tukums', 'talsi', 'saldus', 'kuldiga', 'dobele', 'bauska', 'aizkraukle', 'aluksne', 'balvi', 'cesis', 'gulbene', 'kraslava', 'limbazi', 'ludza', 'madona', 'preili', 'sigulda', 'smiltene', 'valka', 'marupe', 'olaine', 'salaspils', 'adazi'];

export default function ClassifiedsPage() {
  const { tr } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState(CLASSIFIED_CATEGORIES);
  const [search, setSearch] = useState(sp.get('search') || '');
  const [cartN, setCartN] = useState(clsCartCount());
  const category = sp.get('category') || '';
  const subcat = sp.get('subcat') || '';
  const region = sp.get('region') || '';
  const sort = sp.get('sort') || 'newest';
  const priceMinQ = sp.get('price_min') || '';
  const priceMaxQ = sp.get('price_max') || '';
  const [pMin, setPMin] = useState(priceMinQ);
  const [pMax, setPMax] = useState(priceMaxQ);
  const [savingSearch, setSavingSearch] = useState(false);
  const catMap = Object.fromEntries(cats.map((c) => [c.id, c]));

  // Merged taxonomy (fixed + admin-approved customs) + drill-down rows.
  const catTree = useMemo(() => mergeCustomCategories(cats.filter((c) => c.custom), CLASSIFIED_TAXONOMY), [cats]);
  const activePath = subcat ? categoryPath(subcat, catTree) : (category ? [category] : []);
  const catRows = [{ depth: 0, opts: categoryChildren(null, catTree), active: activePath[0] || '', parent: null }];
  for (let d = 0; d < activePath.length; d += 1) {
    const kids = categoryChildren(activePath[d], catTree);
    if (kids && kids.length) catRows.push({ depth: d + 1, opts: kids, active: activePath[d + 1] || '', parent: activePath[d] });
  }

  useEffect(() => {
    const onChange = () => setCartN(clsCartCount());
    window.addEventListener(CLS_CART_EVENT, onChange);
    return () => window.removeEventListener(CLS_CART_EVENT, onChange);
  }, []);

  useEffect(() => {
    axios.get(`${API}/classifieds/meta/categories`).then(({ data }) => setCats(data.categories || CLASSIFIED_CATEGORIES)).catch(() => {});
  }, []);

  const setParam = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next);
  };

  const setParams = (obj) => {
    const next = new URLSearchParams(sp);
    Object.entries(obj).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setSp(next);
  };

  // depth 0 = top category; deeper = subcategory within the active path.
  const pickCat = (depth, id) => {
    if (depth === 0) setParams({ category: id, subcat: '' });
    else setParams({ category: activePath[0] || category, subcat: id });
  };
  const pickAll = (depth) => {
    if (depth === 0) { setParams({ category: '', subcat: '' }); return; }
    const parent = activePath[depth - 1];
    if (depth - 1 === 0) setParams({ category: parent, subcat: '' });
    else setParams({ category: activePath[0], subcat: parent });
  };

  useEffect(() => {
    const ae = typeof document !== 'undefined' ? document.activeElement : null;
    const tid = ae && ae.getAttribute ? ae.getAttribute('data-testid') : null;
    if (tid !== 'price-min-input') setPMin(sp.get('price_min') || '');
    if (tid !== 'price-max-input') setPMax(sp.get('price_max') || '');
  }, [sp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (subcat) params.subcategory = subcat;
      if (region) params.region = region;
      if (sp.get('search')) params.search = sp.get('search');
      if (sp.get('price_min')) params.price_min = sp.get('price_min');
      if (sp.get('price_max')) params.price_max = sp.get('price_max');
      if (sort && sort !== 'newest') params.sort = sort;
      const { data } = await axios.get(`${API}/classifieds`, { params });
      setItems(data || []);
    } catch (e) { setItems([]); }
    setLoading(false);
  }, [category, subcat, region, sort, sp]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => { e.preventDefault(); setParam('search', search.trim()); };
  const applyPrice = () => setParams({ price_min: pMin || '', price_max: pMax || '' });
  const hasFilters = category || subcat || region || sp.get('search') || sp.get('price_min') || sp.get('price_max');

  // Human label for a saved search, built from the active filters.
  const buildSearchLabel = () => {
    const parts = [];
    const leaf = subcat || category;
    if (leaf) { const n = resolveCat(leaf); parts.push(tr(n.lv, n.en, n.ru)); }
    if (region) parts.push(regionLabel(region));
    const pmin = sp.get('price_min'); const pmax = sp.get('price_max');
    if (pmin || pmax) parts.push(`€${pmin || '0'}–${pmax || '∞'}`);
    const s = sp.get('search'); if (s) parts.push(`"${s}"`);
    return parts.join(' · ') || tr('Visi sludinājumi', 'All listings', 'Все объявления');
  };

  const saveSearch = async () => {
    if (!user) { navigate('/login'); return; }
    if (!hasFilters) { toast(tr('Pievieno vismaz vienu filtru, lai saglabātu', 'Add at least one filter to save', 'Добавьте хотя бы один фильтр')); return; }
    setSavingSearch(true);
    try {
      await axios.post(`${API}/classifieds/saved-searches`, {
        label: buildSearchLabel(),
        category: category || null,
        subcategory: subcat || null,
        region: region || null,
        search: sp.get('search') || null,
        price_min: sp.get('price_min') ? Number(sp.get('price_min')) : null,
        price_max: sp.get('price_max') ? Number(sp.get('price_max')) : null,
      });
      toast.success(tr('Meklējums saglabāts! Saņemsi paziņojumu par jauniem atbilstošiem sludinājumiem.', 'Search saved! You will be notified about new matching listings.', 'Поиск сохранён! Вы получите уведомление о новых объявлениях.'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr('Kļūda', 'Error', 'Ошибка'));
    }
    setSavingSearch(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="classifieds-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#2F7E79] to-[#1F5F5A] text-white">
        <div className="max-w-6xl mx-auto px-5 py-10 sm:py-14">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold bg-white/15 rounded-full px-3 py-1 mb-3"><Tag className="w-4 h-4" /> C2C</div>
              <h1 className="font-outfit text-4xl sm:text-5xl font-bold leading-tight">{tr('Privātie sludinājumi', 'Classifieds', 'Частные объявления')}</h1>
              <p className="mt-2 text-white/80 text-base max-w-xl">{tr('Pērc un pārdod ātri starp privātpersonām — bez maksas.', 'Buy and sell quickly between individuals — free of charge.', 'Покупай и продавай быстро между частными лицами — бесплатно.')}</p>
            </div>
            <Button onClick={() => navigate(user ? '/sludinajumi/jauns' : '/login')}
              className="bg-white text-[#2F7E79] hover:bg-white/90 font-semibold gap-2 h-11 px-5 rounded-full" data-testid="add-classified-hero-btn">
              <Plus className="w-5 h-5" /> {tr('Pievienot sludinājumu', 'Post a listing', 'Добавить объявление')}
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => navigate('/cart')}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-semibold transition-colors" data-testid="classifieds-cart-link">
              <ShoppingCart className="w-4 h-4" /> {tr('Grozs', 'Cart', 'Корзина')}{cartN > 0 && <span className="bg-white text-[#2F7E79] rounded-full px-1.5 text-xs">{cartN}</span>}
            </button>
            {user && (
              <button onClick={() => navigate('/sludinajumi/darijumi')}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-semibold transition-colors" data-testid="classifieds-deals-link">
                <ShieldCheck className="w-4 h-4" /> {tr('Mani darījumi', 'My deals', 'Мои сделки')}
              </button>
            )}
            {user && (
              <button onClick={() => navigate('/sludinajumi/meklejumi')}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-semibold transition-colors" data-testid="classifieds-saved-link">
                <Bookmark className="w-4 h-4" /> {tr('Saglabātie meklējumi', 'Saved searches', 'Сохранённые')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-8">
        {/* Search + sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <form onSubmit={submitSearch} className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={tr('Meklēt sludinājumus...', 'Search listings...', 'Искать объявления...')}
              className="w-full h-12 pl-12 pr-4 rounded-full border border-[#E5E4E0] bg-white text-[#121212] focus:outline-none focus:border-[#2F7E79]"
              data-testid="classifieds-search-input" />
          </form>
          <Select value={sort} onValueChange={(v) => setParam('sort', v === 'newest' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-52 h-12 rounded-full border-[#E5E4E0] bg-white" data-testid="classifieds-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{tr('Jaunākie', 'Newest', 'Новые')}</SelectItem>
              <SelectItem value="price_asc">{tr('Lētākie', 'Cheapest', 'Дешевле')}</SelectItem>
              <SelectItem value="price_desc">{tr('Dārgākie', 'Most expensive', 'Дороже')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Category drill-down (multi-level) */}
        <div className="space-y-2 mb-4" data-testid="classifieds-categories">
          {catRows.map((row) => (
            <div key={row.depth} className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid={`category-row-${row.depth}`}>
              <button onClick={() => pickAll(row.depth)}
                className={`flex-shrink-0 h-9 px-4 rounded-full text-sm font-semibold transition-colors ${!row.active ? 'bg-[#2F7E79] text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#2F7E79]'}`}
                data-testid={row.depth === 0 ? 'category-chip-all' : `subcategory-chip-all-${row.depth}`}>
                {row.depth === 0 ? tr('Visi', 'All', 'Все') : tr('Visi', 'All', 'Все')}
              </button>
              {row.opts.map((c) => (
                <button key={c.id} onClick={() => pickCat(row.depth, c.id)}
                  className={`flex-shrink-0 h-9 px-4 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${row.active === c.id ? 'bg-[#2F7E79] text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#2F7E79]'}`}
                  data-testid={`category-chip-${c.id}`}>
                  {row.depth === 0 && <span>{c.emoji || '📦'}</span>} {tr(c.lv, c.en, c.ru)}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Region + price filter + save + clear */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Select value={region || 'all'} onValueChange={(v) => setParam('region', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-56 h-10 rounded-full border-[#E5E4E0] bg-white" data-testid="classifieds-region-select">
              <SelectValue placeholder={tr('Visi novadi', 'All regions', 'Все районы')} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">{tr('Visi novadi', 'All regions', 'Все районы')}</SelectItem>
              {LV_REGIONS.map((r) => <SelectItem key={r} value={r}>{regionLabel(r)}</SelectItem>)}
            </SelectContent>
          </Select>

          <form onSubmit={(e) => { e.preventDefault(); applyPrice(); }} className="flex items-center gap-2" data-testid="classifieds-price-filter">
            <span className="text-sm text-[#5C5C5C]">{tr('Cena', 'Price', 'Цена')}</span>
            <input type="number" min="0" inputMode="decimal" value={pMin} onChange={(e) => setPMin(e.target.value)} onBlur={applyPrice}
              placeholder={tr('no €', 'from €', 'от €')} className="w-24 h-10 px-3 rounded-full border border-[#E5E4E0] bg-white text-sm focus:outline-none focus:border-[#2F7E79]" data-testid="price-min-input" />
            <span className="text-[#8A8A8A]">–</span>
            <input type="number" min="0" inputMode="decimal" value={pMax} onChange={(e) => setPMax(e.target.value)} onBlur={applyPrice}
              placeholder={tr('līdz €', 'to €', 'до €')} className="w-24 h-10 px-3 rounded-full border border-[#E5E4E0] bg-white text-sm focus:outline-none focus:border-[#2F7E79]" data-testid="price-max-input" />
          </form>

          <Button onClick={saveSearch} disabled={savingSearch} variant="outline"
            className="h-10 rounded-full border-[#2F7E79] text-[#2F7E79] hover:bg-[#E6F2F0] gap-2" data-testid="save-search-btn">
            {savingSearch ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}
            {tr('Saglabāt meklējumu', 'Save search', 'Сохранить поиск')}
          </Button>

          {hasFilters && (
            <button onClick={() => { setSearch(''); setPMin(''); setPMax(''); setSp(new URLSearchParams()); }}
              className="inline-flex items-center gap-1 text-sm text-[#5C5C5C] hover:text-[#121212]" data-testid="clear-filters-btn">
              <X className="w-4 h-4" /> {tr('Notīrīt filtrus', 'Clear filters', 'Сбросить')}
            </button>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2F7E79]" /></div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center" data-testid="classifieds-empty">
            <div className="text-5xl mb-3">🧳</div>
            <p className="text-[#5C5C5C]">{tr('Nav atrastu sludinājumu.', 'No listings found.', 'Объявления не найдены.')}</p>
            <Button onClick={() => navigate(user ? '/sludinajumi/jauns' : '/login')} className="mt-4 bg-[#2F7E79] hover:bg-[#276b66] text-white gap-2 rounded-full" data-testid="add-classified-empty-btn">
              <Plus className="w-4 h-4" /> {tr('Pievienot pirmo', 'Post the first one', 'Добавить первое')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="classifieds-grid">
            {items.map((it) => <ClassifiedCard key={it.id} item={it} catMap={catMap} />)}
          </div>
        )}
      </div>
    </div>
  );
}
